import { forwardRef, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePublishedUpdates, useMyUpdateViews, useMarkUpdateSeen, useMarkUpdateDismissed, useMarkUpdateClicked, UPDATE_TYPE_LABELS, resolveUpdateContent } from "@/hooks/useAppUpdates";
import type { AppUpdate } from "@/hooks/useAppUpdates";
import { X, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/lib/i18n";

const UpdateBanner = forwardRef<HTMLDivElement>((_props, ref) => {
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const { data: updates = [] } = usePublishedUpdates();
  const { data: views = [] } = useMyUpdateViews();
  const markSeen = useMarkUpdateSeen();
  const markDismissed = useMarkUpdateDismissed();
  const markClicked = useMarkUpdateClicked();
  const navigate = useNavigate();
  const [banner, setBanner] = useState<AppUpdate | null>(null);
  const [seenIds, setSeenIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user || updates.length === 0) return;
    const dismissedIds = new Set(views.filter(v => v.dismissed_at).map(v => v.update_id));
    const bannerUpdate = updates.find(u => u.display_banner && !dismissedIds.has(u.id));
    if (bannerUpdate) {
      setBanner(bannerUpdate);
      if (!seenIds.has(bannerUpdate.id)) {
        setSeenIds(prev => new Set(prev).add(bannerUpdate.id));
        markSeen.mutate({ updateId: bannerUpdate.id, method: "banner" });
      }
    }
  }, [user, updates, views]);

  if (!banner) return null;

  const typeInfo = UPDATE_TYPE_LABELS[banner.update_type] || UPDATE_TYPE_LABELS.improvement;
  const content = resolveUpdateContent(banner, locale);

  return (
    <div ref={ref} className="bg-primary/5 border-b border-primary/10 px-4 py-2.5 flex items-center gap-3">
      <span className="text-lg shrink-0">{typeInfo.emoji}</span>
      <p className="flex-1 text-sm font-medium truncate">
        <span className="font-semibold">{content.title}</span>
        {content.short_description && (
          <span className="text-muted-foreground ml-1 hidden sm:inline">– {content.short_description}</span>
        )}
      </p>
      {banner.action_url && (
        <button
          onClick={() => { markClicked.mutate(banner.id); navigate(banner.action_url!); }}
          className="text-primary text-sm font-medium flex items-center gap-0.5 shrink-0 hover:underline"
        >
          {t("whatsNew.readMore" as any)} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        onClick={() => { markDismissed.mutate(banner.id); setBanner(null); }}
        className="text-muted-foreground hover:text-foreground shrink-0 p-1"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
});

UpdateBanner.displayName = "UpdateBanner";

export default UpdateBanner;
